import {
  CheckOutlined,
  ClockCircleOutlined,
  EditOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { useHead } from '@unhead/react';
import {
  App,
  Flex,
  Input,
  Popconfirm,
  Space,
  Statistic,
  Tooltip,
  Typography,
} from 'antd';
import classNames from 'classnames';
import groupBy from 'lodash.groupby';
import isEqual from 'lodash.isequal';
import { useEffect, useState } from 'react';
import {
  Link,
  createSearchParams,
  generatePath,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import invariant from 'tiny-invariant';
import { i18n } from 'translations';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import { nameWithPreviousActivityNameAndDate } from 'business/shift-report/activity/services';
import ExitReportButton from 'business/shift-report/report/components/exit-report-button';
import ReportChart from 'business/shift-report/report/components/ReportChart';
import EmptyZoneModal from 'business/shift-report/report/components/ReportChart/EmptyZoneModal';
import { EmptyZone } from 'business/shift-report/report/components/ReportChart/services/metadata/types';
import { MassValidationDrawer } from 'business/shift-report/report/pages/ReportEdit/components/mass-validation-drawer';
import NewTaskDrawer from 'business/shift-report/report/pages/ReportEdit/components/new-task-drawer';
import { NotEndedTasksWarning } from 'business/shift-report/report/pages/ReportEdit/components/not-ended-tasks-warning';
import ReportMetadataDrawer from 'business/shift-report/report/pages/ReportEdit/components/report-metadata-drawer';
import { excavationAndBuildTasksWithoutEnd } from 'business/shift-report/report/pages/ReportEdit/services/excavation-and-build-tasks-without-end';
import { getLastFinishedRing } from 'business/shift-report/report/services/tasksOperations';
import { parseReportDateString } from 'business/shift-report/report/services/timeOperations';
import Routes from 'config/routes';
import {
  ActivityInvertedTreeFullFragment,
  Maybe,
  Module_Enum,
  Task,
  Task_Updates,
  TaskFullFragment,
  useDeleteTaskMutation,
  useGenerateShiftReportPdfMutation,
  useGetPreviousReportTasksQuery,
  useGetShiftReportMetadataQuery,
  useInvalidateOperatorValidationMutation,
  useManagerReportUpdateMutation,
  useShiftReportNoteUpdateMutation,
  useUpdateAndValidateTasksMutation,
} from 'generated/graphql';
import logger from 'technical/logger';
import { NotFound } from 'technical/router/switch/reporting-no-match';
import Button from 'ui/button';
import Loader from 'ui/loader';

import AutofillButton from './components/autofill-button';
import ReportChartHeaderInfo from './components/header-info';
import pageStyle from './index.module.scss';
import {
  formatRingInvalidText,
  getShiftTotalTime,
} from './services/chart-page';
import {
  getAuthorizedOperations,
  isAutofillOperationEnabled,
  isEditShiftReportMetaEnabled,
  isManagerValidationEnabled,
  isNoteEditionOperationEnabled,
  isOnlyOperatorValidationEnabled,
  isPreviewEnabled,
  isTaskOperationEnabled,
  isUserInvalidationEnabled,
} from './services/permissions';

const { Title } = Typography;

type UniqueTask = {
  activityId: string;
  critical: boolean;
  duration?: Maybe<number>;
  endDate: string;
  ended: boolean;
  isValidated: boolean;
  ring: number;
  startDate: string;
};

const fields: (keyof UniqueTask)[] = [
  'activityId',
  'critical',
  'duration',
  'endDate',
  'ended',
  'isValidated',
  'ring',
  'startDate',
];

const getReportPath = (
  route: string,
  reportId: string,
  previousRedirection?: string,
) => {
  if (!previousRedirection) {
    const redirect = createSearchParams({
      redirect: generatePath(Routes.ReportEdit, { id: reportId }),
    }).toString();

    return `${route}?${redirect}`;
  }

  const previousRedirect = createSearchParams({
    redirect: previousRedirection,
  }).toString();
  const redirect = createSearchParams({
    redirect: `${generatePath(Routes.ReportEdit, { id: reportId })}?${previousRedirect}`,
  }).toString();

  return `${route}?${redirect}`;
};

function ReportEditPage() {
  const { t } = useTranslation();

  useHead({ title: t('user.menu.sections.reporting.title') });

  const { message } = App.useApp();

  const { user, currentConstructionSite, constructionSites } = useAppContext();
  const {
    modules: {
      SHIFT_REPORT: { isConstructionSiteUser, hasManagerEditPermission },
    },
  } = usePermissions();
  const navigate = useNavigate();
  const { id: shiftReportId } = useParams<{ id: string }>();
  invariant(shiftReportId, "id isn't set within the route");
  const headers = useGraphqlApiHeaders(Module_Enum.ShiftReport);

  const [searchParams] = useSearchParams();
  const routeRedirection = searchParams.get('redirect') ?? undefined;

  let lastCriticalTask: TaskFullFragment | undefined;
  let totalCriticalTasksTimeInMinutes = 0;
  let shiftTimeInMinutes = 0;

  const [massValidationVisible, setMassValidationVisible] = useState(false);

  const [taskDrawerVisibility, setTaskDrawerVisibility] =
    useState<boolean>(false);
  const [drawerTask, setDrawerTask] = useState<TaskFullFragment>();
  const [drawerTaskTime, setDrawerTaskTime] = useState<[string, string]>();
  const [drawerTaskActivity, setDrawerTaskActivity] =
    useState<ActivityInvertedTreeFullFragment>();
  const [emptyZoneModalVisibility, setEmptyZoneModalVisibility] =
    useState(false);
  const [emptyZoneModalData, setEmptyZoneModalData] = useState<EmptyZone>();
  const [reportMetadataDrawerVisibility, setReportMetadataDrawerVisibility] =
    useState<boolean>(false);
  const [reportNote, setReportNote] = useState<string>('');
  const [criticalPathValid, setCriticalPathValid] = useState<boolean>(true);
  const [ringInvalid, setRingInvalid] = useState<string[] | undefined>(
    undefined,
  );
  const [notValidatedTasks, setNotValidatedTasks] = useState<
    string[] | undefined
  >(undefined);
  const [deleteTask] = useDeleteTaskMutation({
    context: {
      headers,
    },
  });

  const {
    loading,
    error,
    data,
    refetch: refreshReportData,
  } = useGetShiftReportMetadataQuery({
    variables: {
      shiftReportId,
      constructionSiteId: currentConstructionSite?.id,
    },
    context: { headers },
    skip: !currentConstructionSite?.id,
    fetchPolicy: 'cache-and-network',
  });

  const uploadedTaskFiles =
    data?.shiftReport?.tasks?.map((task) => ({
      taskId: task.id,
      uploadedFilesCount: task.files?.length ?? 0,
    })) ?? [];

  const [shiftReportNoteUpdate] = useShiftReportNoteUpdateMutation({
    context: { headers },
  });
  const [managerValidation] = useManagerReportUpdateMutation({
    context: { headers },
  });
  const [invalidateOperatorValidation] =
    useInvalidateOperatorValidationMutation({ context: { headers } });

  const [generatePdf] = useGenerateShiftReportPdfMutation({
    context: { headers },
  });

  useEffect(
    function alertUser() {
      if (error) {
        message.error(t('errors.error_generic'));
      }
    },
    [error, message, t],
  );

  const report = data?.shiftReport ? data.shiftReport : undefined;

  useEffect(() => {
    if (report && drawerTask) {
      const reportTask = report.tasks.find(({ id }) => id === drawerTask.id);
      const equalResult = isEqual(reportTask, drawerTask);
      if (!equalResult) {
        setDrawerTask(reportTask);
      }
    }
  }, [report, drawerTask]);

  const { data: dataPrevious } = useGetPreviousReportTasksQuery({
    variables: {
      constructionSiteId: currentConstructionSite?.id,
      date: report?.date,
      shiftReportId: report?.id,
      shiftStartTime: report?.shift.startTime,
    },
    context: { headers },
    skip: !currentConstructionSite?.id,
  });

  const previousReportLastFinishedRing =
    dataPrevious &&
    dataPrevious.shiftReport.length > 0 &&
    dataPrevious.shiftReport[0].lastFinishedRing != null
      ? dataPrevious.shiftReport[0].lastFinishedRing
      : undefined;

  const removeTask = async (taskId: string) => {
    try {
      await deleteTask({
        variables: {
          id: taskId,
        },
      });
    } catch (newTaskError) {
      message.error(
        t('pages.report.chart.drawer.taskMutationFailed', {
          context: 'delete',
        }),
      );
      logger.error(newTaskError);
    }
  };

  const isTaskUnique = (testedTask: UniqueTask, uniqueTasks: UniqueTask[]) =>
    !uniqueTasks.some((uniqueTask) =>
      fields.every((field) => testedTask[field] === uniqueTask[field]),
    );

  const removeTaskDuplicates = async () => {
    const tasks = report?.tasks ?? [];
    const uniqueTasks: typeof tasks = [];
    const duplicatedTaskIds = tasks.reduce((acc, currentTask) => {
      if (isTaskUnique(currentTask, uniqueTasks)) {
        uniqueTasks.push(currentTask);
        return acc;
      }
      return [...acc, currentTask.id];
    }, [] as string[]);

    if (duplicatedTaskIds.length > 0) {
      try {
        await Promise.all(duplicatedTaskIds.map(removeTask));
        refreshReportData();
      } catch (e) {
        logger.error(e);
      }
    }
  };

  const checkRingProgression = () => {
    const tasks = report?.tasks ?? [];
    const { criticalTasks = [], nonCriticalTasks = [] } = groupBy(
      tasks,
      (task) => (task.critical ? 'criticalTasks' : 'nonCriticalTasks'),
    );
    const invalidTasks: typeof tasks = [];

    const lastFinishedRing = previousReportLastFinishedRing;
    let previousTaskRing = -Infinity;
    criticalTasks.forEach((task) => {
      // critical tasks should always have a ring number equal or greater than the preceding critical task
      if (
        (lastFinishedRing !== undefined && task.ring <= lastFinishedRing) ||
        task.ring < previousTaskRing
      ) {
        invalidTasks.push(task);
      }
      previousTaskRing = task.ring;
    });

    function isBetween(date: Date, range: { start: Date; end: Date }) {
      return date >= range.start && date <= range.end;
    }
    function hasSameRingNumber(
      task: Pick<Task, 'ring' | 'startDate' | 'endDate'>,
    ) {
      return (criticalTask: Pick<Task, 'ring' | 'startDate' | 'endDate'>) => {
        const areConcurrentTasks =
          isBetween(task.startDate, {
            start: criticalTask.startDate,
            end: criticalTask.endDate,
          }) ||
          isBetween(task.endDate, {
            start: criticalTask.startDate,
            end: criticalTask.endDate,
          }) ||
          isBetween(criticalTask.startDate, {
            start: task.startDate,
            end: task.endDate,
          }) ||
          isBetween(criticalTask.endDate, {
            start: task.startDate,
            end: task.endDate,
          });

        if (!areConcurrentTasks) {
          return false;
        }

        return task.ring === criticalTask.ring;
      };
    }
    nonCriticalTasks.forEach((task) => {
      // non critical tasks should always have a ring number equal to one of the concurrent critical task
      if (!criticalTasks.some(hasSameRingNumber(task))) {
        invalidTasks.push(task);
      }
    });

    if (invalidTasks.length > 0) {
      setRingInvalid(
        invalidTasks.map(
          (task) =>
            `[${nameWithPreviousActivityNameAndDate(task).join(' / ')}]`,
        ),
      );
    } else if (ringInvalid && invalidTasks.length === 0) {
      setRingInvalid(undefined);
    }

    const tasksWithoutValidation = tasks.filter(
      (task) => task.isValidated === false,
    );
    if (tasksWithoutValidation.length > 0) {
      setNotValidatedTasks(
        tasksWithoutValidation.map(
          (task) =>
            `[${nameWithPreviousActivityNameAndDate(task).join(' / ')}]`,
        ),
      );
    } else {
      setNotValidatedTasks([]);
    }
  };

  // WARNING: i disabled this because i'm not confident enough to add all the required dependencies
  // for this use effect, it may lead to some bugs, and now it works as intended
  useEffect(() => {
    removeTaskDuplicates()
      .catch((e) => logger.error(e))
      .finally(() => checkRingProgression());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const criticalTasks = report?.tasks.filter((task) => task.critical);
  if (report) {
    criticalTasks?.forEach((task) => {
      if (task.duration) {
        totalCriticalTasksTimeInMinutes += task.duration;
      }
    });

    shiftTimeInMinutes = getShiftTotalTime(
      parseReportDateString(report.date, report.shift.startTime),
      report.shift,
    );
    lastCriticalTask =
      criticalTasks && criticalTasks.length > 0
        ? criticalTasks[criticalTasks.length - 1]
        : undefined;
  }

  const handleManagerShiftReportValidation = async () => {
    const note = reportNote || report?.note;
    const lastFinishedRing = getLastFinishedRing(report?.tasks ?? []);
    try {
      await managerValidation({
        variables: { id: shiftReportId, note, lastFinishedRing },
      });
      message.success(t('pages.report.chart.updateShiftReportSuccess'));

      generatePdf({
        variables: {
          id: shiftReportId,
          language: i18n.language,
        },
      }).catch((mutationError) => logger.error(mutationError));

      navigate(generatePath(Routes.ReportEditEnd, { id: shiftReportId }));
    } catch (err) {
      message.error(t('pages.report.chart.updateShiftReportFailed'));
      logger.error(err);
    }
  };

  const handleInvalidateOperatorValidation = async () => {
    try {
      const { errors: invalidateErrors } = await invalidateOperatorValidation({
        variables: { id: shiftReportId },
      });
      if (invalidateErrors) {
        message.error(t('pages.report.chart.invalidateShiftReportFailed'));
        logger.error(invalidateErrors);
      } else {
        message.success(t('pages.report.chart.invalidateShiftReportSuccess'));
        await refreshReportData();
      }
    } catch (err) {
      message.error(t('pages.report.chart.invalidateShiftReportFailed'));
      logger.error(err);
    }
  };

  const handleNoteUpdate = async (value: string) => {
    setReportNote(value);
    try {
      await shiftReportNoteUpdate({
        variables: { id: shiftReportId, note: value },
      });
    } catch (err) {
      message.error(t('pages.report.chart.updateShiftReportNoteFailed'));
      logger.error(err);
    }
  };

  const getWarningText = (totalTimeInvalid: boolean) => {
    if (notValidatedTasks && notValidatedTasks?.length > 0) {
      return t('pages.report.chart.operatorConfirmToValidate', {
        string: formatRingInvalidText(notValidatedTasks),
      });
    }
    if (totalTimeInvalid && ringInvalid) {
      return t('pages.report.chart.operatorConfirmTimeAndRing', {
        string: formatRingInvalidText(ringInvalid),
        count: ringInvalid.length,
      });
    }
    if (ringInvalid) {
      return t('pages.report.chart.operatorConfirmRing', {
        string: formatRingInvalidText(ringInvalid),
        count: ringInvalid.length,
      });
    }
    return t('pages.report.chart.operatorConfirmTime');
  };

  const handleEmptyZoneClick = (_event: any, emptyZoneData: EmptyZone) => {
    setEmptyZoneModalVisibility(true);
    setEmptyZoneModalData(emptyZoneData);
  };

  const handleTaskEdit = (_event: any, task: TaskFullFragment) => {
    if (task) {
      setDrawerTask(task);
      setTaskDrawerVisibility(true);
    }
  };

  const duplicateTask = () => {
    setDrawerTaskActivity(drawerTask?.activity);
    setDrawerTask(undefined);
    setDrawerTaskTime(undefined);
  };

  const newTaskFromTime = (startTime: string, endTime: string) => {
    if (startTime && endTime) {
      setDrawerTaskTime([startTime, endTime]);
      setTaskDrawerVisibility(true);
    }
  };

  const closeTaskDrawer = () => {
    setTaskDrawerVisibility(false);
  };

  const closeReportMetadataDrawer = () => {
    refreshReportData();
    setReportMetadataDrawerVisibility(false);
  };

  const resetDrawer = () => {
    setDrawerTask(undefined);
    setDrawerTaskTime(undefined);
    setDrawerTaskActivity(undefined);
  };

  const isReportComplete = () => {
    const isCriticalTimeValid =
      totalCriticalTasksTimeInMinutes === shiftTimeInMinutes;
    const hasNonValidatedTask = report?.tasks.some(
      ({ isValidated }) => isValidated === false,
    );

    return isCriticalTimeValid && !ringInvalid && !hasNonValidatedTask;
  };

  const enabledOperations = getAuthorizedOperations({
    user,
    report,
    constructionSite: currentConstructionSite || undefined,
    isConstructionSiteUser,
    hasManagerEditPermission,
  });

  const [updateAndValidateTasks] = useUpdateAndValidateTasksMutation({
    onError: () => {
      message.open({
        content: t('pages.report.massValidation.error'),
        type: 'error',
      });
    },
    context: { headers },
  });

  if (
    !loading &&
    !report?.constructionSite &&
    !constructionSites.find(({ id }) => id === report?.constructionSite.id)
  ) {
    return <NotFound />;
  }

  const reportIsComplete = isReportComplete();

  const ValidationOptions = ({
    isManagerValidation,
  }: {
    isManagerValidation: boolean;
  }) => {
    const notEndedTasks = excavationAndBuildTasksWithoutEnd(report);
    if (notEndedTasks?.length) {
      return (
        <Popconfirm
          title={<NotEndedTasksWarning notEndedTasks={notEndedTasks} />}
          onCancel={() => {}}
          okText={t('pages.report.chart.managerValidate')}
          cancelText={t('common.cancel')}
          okButtonProps={{
            disabled: true,
          }}
        >
          <div>
            <Button type="primary" icon={<CheckOutlined />}>
              {t('pages.report.chart.validate')}
            </Button>
          </div>
        </Popconfirm>
      );
    }

    // Manager is validating a report
    if (reportIsComplete && isManagerValidation) {
      return (
        <Popconfirm
          title={t('pages.report.chart.managerConfirm')}
          onConfirm={handleManagerShiftReportValidation}
          cancelText={t('common.cancel')}
        >
          <div>
            <Button type="primary" icon={<CheckOutlined />}>
              {t('pages.report.chart.managerValidate')}
            </Button>
          </div>
        </Popconfirm>
      );
    }

    // Operator validation
    if (reportIsComplete) {
      // Validate to end form (report is complete)
      return (
        <Link to={Routes.ReportEditFinishing.replace(':id', shiftReportId)}>
          <Button type="primary" icon={<CheckOutlined />}>
            {t('pages.report.chart.validate')}
          </Button>
        </Link>
      );
    }

    // Popup confirm if not complete
    return (
      <Popconfirm
        title={getWarningText(
          totalCriticalTasksTimeInMinutes !== shiftTimeInMinutes,
        )}
        onCancel={() => {}}
        okText={t('pages.report.chart.managerValidate')}
        cancelText={t('common.cancel')}
        okButtonProps={{
          disabled: true,
        }}
      >
        <div>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => {
              setCriticalPathValid(
                totalCriticalTasksTimeInMinutes === shiftTimeInMinutes,
              );
            }}
          >
            {t('pages.report.chart.validate')}
          </Button>
        </div>
      </Popconfirm>
    );
  };

  const previewPagePath = getReportPath(
    generatePath(Routes.ReportView, {
      id: shiftReportId,
    }),
    shiftReportId,
    routeRedirection,
  );

  return (
    <Flex
      flex={1}
      vertical
      gap={20}
      align="center"
      className={classNames(pageStyle.reportEditPage, 'page-appear')}
    >
      {/* Header Informations */}
      {report && (
        <>
          <ReportChartHeaderInfo report={report} />
          <div className={pageStyle.wrapperStatsTasksCta}>
            {isTaskOperationEnabled(enabledOperations) && (
              <div className={pageStyle.itemSpacer} />
            )}
            <Statistic
              className={pageStyle.statsInfo}
              title={t('pages.report.chart.totalTime')}
              value={totalCriticalTasksTimeInMinutes}
              suffix={`/ ${shiftTimeInMinutes} min`}
              prefix={<ClockCircleOutlined />}
              groupSeparator=" "
            />
            {(isTaskOperationEnabled(enabledOperations) ||
              isAutofillOperationEnabled(enabledOperations)) && (
              <div className={pageStyle.itemTasksCta}>
                {isTaskOperationEnabled(enabledOperations) && (
                  <Button
                    onClick={() => setTaskDrawerVisibility(true)}
                    type="primary"
                    className={pageStyle.taskCta}
                    icon={<PlusCircleOutlined />}
                  >
                    {t('pages.report.chart.addNewTaskBtnLabel')}
                  </Button>
                )}
                {isAutofillOperationEnabled(enabledOperations) && (
                  <div className={pageStyle.wrapperColumnTaskCta}>
                    <AutofillButton
                      shiftReportId={shiftReportId}
                      previousTask={lastCriticalTask}
                      report={report}
                      refreshReportData={refreshReportData}
                      autofillApiId={currentConstructionSite?.autofillApiId}
                      autofillOpcuaEnabled={
                        currentConstructionSite?.autofillOpcuaEnabled
                      }
                    />
                  </div>
                )}
                {isTaskOperationEnabled(enabledOperations) &&
                  isAutofillOperationEnabled(enabledOperations) && (
                    <Button
                      onClick={() => setMassValidationVisible(true)}
                      disabled={
                        !report?.tasks.some((task) => !task.isValidated)
                      }
                    >
                      {t('pages.report.chart.validateTasks')}
                    </Button>
                  )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Shift Report Chart */}
      {!data && loading && <Loader />}
      {data && report && (
        <ReportChart
          shift={report.shift}
          date={report.date}
          activities={data.activities}
          tasks={report.tasks}
          externalEventHandlers={
            isTaskOperationEnabled(enabledOperations)
              ? {
                  handleTaskMouseClick: handleTaskEdit,
                  handleEmptyZoneClick,
                }
              : undefined
          }
          criticalPathValid={criticalPathValid}
        />
      )}

      {/* Notes */}
      {isNoteEditionOperationEnabled(enabledOperations) && (
        <div className={pageStyle.footerInfo}>
          <Title level={4}>{t('pages.form.note.label')}</Title>
          {report && (
            <Input.TextArea
              defaultValue={report.note || reportNote}
              onBlur={(e) => handleNoteUpdate(e.currentTarget.innerHTML)}
              style={{ width: '100%' }}
              placeholder={t('pages.form.note.shiftReportPlaceholder')}
              autoSize={{ minRows: 3, maxRows: 8 }}
            />
          )}
        </div>
      )}

      {/* Footer options */}
      <Flex gap={8} wrap align="center" className="button-center">
        {/* Validate to end form (user + manager author) */}
        {isOnlyOperatorValidationEnabled(enabledOperations) ? (
          <ValidationOptions isManagerValidation={false} />
        ) : null}

        {/* Manager validation */}
        {isManagerValidationEnabled(enabledOperations) && (
          <ValidationOptions isManagerValidation={true} />
        )}

        {/* Open metadata editor drawer */}
        {isEditShiftReportMetaEnabled(enabledOperations) && (
          <Button
            icon={<EditOutlined />}
            onClick={() => setReportMetadataDrawerVisibility(true)}
          >
            {t('pages.report.chart.editReportMetadataFields')}
          </Button>
        )}

        {/* Preview report */}
        {isPreviewEnabled(enabledOperations) && (
          <Link to={previewPagePath}>
            <Button type="default" icon={<EyeOutlined />}>
              {t('pages.report.chart.preview')}
            </Button>
          </Link>
        )}

        {/* Close button */}
        <ExitReportButton previousLocation={routeRedirection} />
      </Flex>

      <Space direction="horizontal" align="center" className="button-center">
        {/* Invalidate operator validation */}
        {isUserInvalidationEnabled(enabledOperations) && (
          <Tooltip
            placement="top"
            title={t('pages.report.chart.invalidateOperatorValidationHelper')}
            arrow={{ pointAtCenter: true }}
          >
            <div>
              <Button
                icon={<RollbackOutlined />}
                onClick={handleInvalidateOperatorValidation}
              >
                {t('pages.report.chart.invalidateOperatorValidationButton')}
              </Button>
            </div>
          </Tooltip>
        )}
      </Space>

      {/* Modals/Drawers definition */}
      {report && (
        <>
          <EmptyZoneModal
            emptyZoneData={emptyZoneModalData}
            visible={emptyZoneModalVisibility}
            closeModal={() => setEmptyZoneModalVisibility(false)}
            newTaskFromTime={newTaskFromTime}
          />
          <NewTaskDrawer
            visible={taskDrawerVisibility}
            closeDrawer={closeTaskDrawer}
            resetDrawer={resetDrawer}
            duplicateTask={duplicateTask}
            shiftReportId={shiftReportId}
            activities={data?.activities}
            excavationOrBuildActivities={data?.excavationOrBuildActivities}
            task={drawerTask}
            previousTask={lastCriticalTask}
            criticalTasks={criticalTasks}
            previousRing={previousReportLastFinishedRing}
            taskTime={drawerTaskTime}
            taskActivity={drawerTaskActivity}
            date={parseReportDateString(report.date)}
            shift={report.shift}
            uploadedTaskFiles={uploadedTaskFiles}
          />
          <ReportMetadataDrawer
            shiftReport={report}
            visible={reportMetadataDrawerVisibility}
            closeEndFieldsDrawer={closeReportMetadataDrawer}
            constructionSiteId={currentConstructionSite?.id}
          />
        </>
      )}

      {report && !reportIsComplete ? (
        <MassValidationDrawer
          tasks={report.tasks.filter((task) => !task.isValidated)}
          report={report}
          open={massValidationVisible}
          onCancel={() => setMassValidationVisible(false)}
          onValidate={async (updateTasks: Task_Updates[]) => {
            setMassValidationVisible(false);
            await updateAndValidateTasks({
              variables: {
                updates: updateTasks,
              },
            });
            refreshReportData();
          }}
        />
      ) : null}
    </Flex>
  );
}

export default ReportEditPage;
